<template>
  <v-dialog :model-value="modelValue" max-width="480" @update:model-value="$emit('update:modelValue', $event)">
    <v-card prepend-icon="mdi-account-switch-outline" :title="`Transfer ${entityLabel}`">
      <v-card-text>
        <div v-if="title" class="text-body-2 mb-3">
          Reassign <span class="font-weight-medium">{{ title }}</span> to another member.
        </div>
        <div v-if="currentName" class="text-caption text-grey mb-3">
          Currently assigned to: {{ currentName }}
        </div>
        <v-autocomplete
          density="compact"
          label="New assignee*"
          :items="assigneeItems"
          item-title="title"
          item-value="value"
          v-model="assigneeId"
          clearable
          no-data-text="No other members available"
        ></v-autocomplete>
        <v-textarea
          density="compact"
          label="Note (optional)"
          rows="2"
          v-model="note"
          placeholder="Add context for the handover…"
        ></v-textarea>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text="Cancel" variant="plain" :disabled="saving" @click="close"></v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!assigneeId"
          @click="submit"
        >Transfer</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import apiMixin from '@/mixins/apiMixin';

// Reusable reassignment dialog shared by IT / QA / Sprint ticket views.
// Posts { [idKey]: item[idKey], assignee_id, note, updated_by } to `endpoint`
// and emits `transferred` on success so the parent can refresh.
export default {
  name: 'TransferDialog',
  mixins: [apiMixin],
  props: {
    modelValue: { type: Boolean, default: false },
    // The record being transferred (IT/QA ticket or sprint ticket).
    item: { type: Object, default: null },
    // API endpoint (from constant.js) to POST the transfer to.
    endpoint: { type: String, required: true },
    // Payload key that carries the record id ('id' for IT/QA, 'ticket_id' for sprint).
    idKey: { type: String, default: 'id' },
    // List of active employees: [{ id, fullname }].
    employees: { type: Array, default: () => [] },
    // Id of the current assignee, excluded from the picker.
    currentAssigneeId: { type: [Number, String], default: null },
    // Human-readable name of the current assignee (display only).
    currentName: { type: String, default: '' },
    // Label for the entity type, e.g. 'IT ticket', 'test case', 'ticket'.
    entityLabel: { type: String, default: 'ticket' },
    // Short title of the record shown in the prompt.
    title: { type: String, default: '' },
    // Actor label recorded on the transfer.
    updatedBy: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'transferred'],
  data() {
    return {
      assigneeId: null,
      note: '',
      saving: false,
    };
  },
  computed: {
    assigneeItems() {
      const cur = this.currentAssigneeId != null ? Number(this.currentAssigneeId) : null;
      return this.employees
        .filter(e => Number(e.id) !== cur)
        .map(e => ({ value: e.id, title: e.fullname }));
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.assigneeId = null;
        this.note = '';
        this.saving = false;
      }
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
    },
    submit() {
      if (!this.assigneeId || !this.item) return;
      this.saving = true;
      const payload = {
        [this.idKey]: this.item[this.idKey],
        assignee_id: this.assigneeId,
        note: this.note || null,
        updated_by: this.updatedBy || null,
      };
      axios.post(utils._api(this.endpoint), payload, this.authHeaders())
        .then(() => {
          this.$emit('transferred', this.item);
          this.close();
        })
        .catch((error) => {
          console.error('Transfer failed:', error);
          const msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Transfer failed';
          this.$emit('transferred', null, msg);
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>
